import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, LogOut, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "./shared";
import { useLang, LangToggle } from "./i18n";
import { getUser, clearAuth } from "@/lib/auth";
import ProfileEditModal from "@/components/ProfileEditModal";

export default function Nav({ onStart, offset = false }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(getUser());
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    const h = () => setUser(getUser());
    window.addEventListener("biq-auth", h);
    return () => window.removeEventListener("biq-auth", h);
  }, []);

  const LINKS = [
    { id: "manifesto", label: t.links.manifesto },
    { id: "report", label: t.links.report },
    { id: "journey", label: t.links.journey },
    { id: "stories", label: t.links.stories },
    { id: "faq", label: t.links.faq },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      className={`fixed ${offset ? "top-9" : "top-0"} inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled ? "bg-paper/85 backdrop-blur-xl border-ink/10 shadow-[0_8px_30px_rgba(10,22,51,0.06)]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button onClick={() => go("top")} className="flex items-center gap-3 group" data-testid="nav-logo" aria-label="Bright IQ home">
          <img src="/brightiq-logo.png" alt="Bright IQ logo" className="h-11 w-11 rounded-xl object-contain transition-transform duration-500 group-hover:rotate-6" />
          <span className="font-display font-bold text-xl tracking-tight">
            Bright<span className="text-iqblue">IQ</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-6">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-ink/70 hover:text-ink transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-iqpink after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
          <Link to="/discovery" data-testid="nav-discovery-link" className="text-sm font-semibold text-ink/70 hover:text-iqamber transition-colors duration-300">
            {t.discovery}
          </Link>
          <Link to="/institution" data-testid="nav-institutions-link" className="text-sm font-semibold text-ink/70 hover:text-iqblue transition-colors duration-300">
            {t.institutions}
          </Link>
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard-link" className="text-sm font-semibold text-iqblue hover:text-ink transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-iqblue after:transition-[width] after:duration-300 hover:after:w-full">
                {t.dashboard}
              </Link>
              <button onClick={() => setEditOpen(true)} data-testid="nav-edit-profile-button" title="Edit display name" className="flex items-center gap-2 text-sm font-semibold text-ink/80 border border-ink/15 rounded-full px-4 py-2 hover:border-iqblue hover:text-iqblue transition-colors duration-300">
                {user.name.split(" ")[0]}
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button data-testid="nav-logout-button" onClick={() => clearAuth()} title="Sign out" aria-label="Sign out" className="h-9 w-9 rounded-full border border-ink/15 flex items-center justify-center text-ink/60 hover:border-iqpink hover:text-iqpink transition-colors duration-300">
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <Link to="/assessment" data-testid="nav-signin" className="text-sm font-medium text-ink/70 hover:text-ink transition-colors duration-300">
              {t.signin}
            </Link>
          )}
          <button
            data-testid="nav-cta-button"
            onClick={onStart}
            className="group flex items-center gap-2 bg-ink text-paper text-sm font-semibold pl-5 pr-4 py-2.5 rounded-full hover:bg-iqblue transition-colors duration-300"
          >
            {t.navCta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <LangToggle />
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <LangToggle />
          <button className="p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Toggle menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-paper/95 backdrop-blur-xl border-t border-ink/10"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l.id} data-testid={`mobile-nav-link-${l.id}`} onClick={() => go(l.id)} className="text-left font-display text-lg font-semibold">
                  {l.label}
                </button>
              ))}
              <Link to="/discovery" onClick={() => setOpen(false)} data-testid="mobile-nav-discovery" className="text-left font-display text-lg font-semibold text-iqamber">
                {t.discovery}
              </Link>
              <Link to="/institution" onClick={() => setOpen(false)} data-testid="mobile-nav-institutions" className="text-left font-display text-lg font-semibold">
                {t.institutions}
              </Link>
              {user && (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="mobile-nav-dashboard" className="text-left font-display text-lg font-semibold text-iqblue">
                    {t.dashboard}
                  </Link>
                  <button onClick={() => { setOpen(false); setEditOpen(true); }} data-testid="mobile-nav-edit-profile" className="flex items-center gap-2 text-left font-display text-lg font-semibold">
                    Edit {user.name.split(" ")[0]}'s name <Pencil className="h-4 w-4" />
                  </button>
                </>
              )}
              <Link to="/assessment" onClick={() => setOpen(false)} data-testid="mobile-nav-signin" className="text-left font-display text-lg font-semibold">
                {user ? `${user.name.split(" ")[0]} — Full battery` : t.signin}
              </Link>
              <button data-testid="mobile-nav-cta" onClick={() => { setOpen(false); onStart(); }} className="bg-ink text-paper font-semibold px-5 py-3 rounded-full w-fit">
                {t.navCta}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      <ProfileEditModal open={editOpen} onClose={() => setEditOpen(false)} user={user} />
    </header>
  );
}
